import { useState, useEffect } from 'react';
import { getGarmentImages } from '@/utils/garmentStorage';

/**
 * Resolves a garment image URL. If it's an idb:// reference,
 * loads the actual data URL from IndexedDB. Otherwise returns as-is.
 */
export function useGarmentImage(url: string | undefined): string | undefined {
  const [resolved, setResolved] = useState<string | undefined>(
    url && !url.startsWith('idb://') ? url : undefined
  );

  useEffect(() => {
    if (!url) {
      setResolved(undefined);
      return;
    }
    if (!url.startsWith('idb://')) {
      setResolved(url);
      return;
    }

    // Parse idb://garmentId/index
    const match = url.match(/^idb:\/\/(.+)\/(\d+)$/);
    if (!match) {
      setResolved(undefined);
      return;
    }

    const [, garmentId, indexStr] = match;
    const index = parseInt(indexStr, 10);

    getGarmentImages(garmentId)
      .then(images => {
        setResolved(images?.[index] ?? undefined);
      })
      .catch(() => setResolved(undefined));
  }, [url]);

  return resolved;
}

/**
 * Resolves multiple garment image URLs from IndexedDB.
 */
export function useGarmentImages(urls: string[] | undefined): string[] {
  const [resolved, setResolved] = useState<string[]>([]);

  useEffect(() => {
    if (!urls || urls.length === 0) {
      setResolved([]);
      return;
    }

    // Check if any need IndexedDB resolution
    const needsIdb = urls.some(u => u.startsWith('idb://'));
    if (!needsIdb) {
      setResolved(urls);
      return;
    }

    // Group by garment ID for efficient fetching
    const promises = urls.map(async (url) => {
      if (!url.startsWith('idb://')) return url;
      const match = url.match(/^idb:\/\/(.+)\/(\d+)$/);
      if (!match) return '';
      const [, garmentId, indexStr] = match;
      const images = await getGarmentImages(garmentId);
      return images?.[parseInt(indexStr, 10)] ?? '';
    });

    Promise.all(promises)
      .then(results => setResolved(results.filter(Boolean)))
      .catch(() => setResolved([]));
  }, [urls?.join(',')]);

  return resolved;
}
